import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_API_KEY = 'opportunityBoard';

export function useInfiniteBoardsQuery({
	pageSize = 50,
	sort = 'asc',
	type,
	expand,
	refetchOnWindowFocus = false,
	meta,
	impersonationContext,
	autoFetchAll = false,
}: {
	pageSize?: number;
	sort?: 'asc' | 'desc';
	type: string;
	expand?: string;
	refetchOnWindowFocus?: boolean;
	meta?: LevStandardQueryMeta;
	impersonationContext?: Api.IImpersonationContext;
	autoFetchAll?: boolean;
}) {
	const userSession = useUserSession();
	// Use impersonationContext from props if provided, otherwise fall back to userSession
	const effectiveImpersonationContext = impersonationContext ?? userSession?.impersonationContext;
	const query = useInfiniteQuery<Api.IPagedCollection<Api.IBoard>>({
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IBoard>>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: {
						pageSize,
						pageToken: pageParam,
						sort,
						type: encodeURIComponent(type),
						...(expand ? { expand } : {}),
					},
					urlPath: `board`,
					impersonationContext: effectiveImpersonationContext,
				}),
				'GET'
			),
		queryKey: [BASE_API_KEY, type, sort, pageSize, effectiveImpersonationContext?.account?.id, expand],
		refetchOnWindowFocus,
		initialPageParam: '',
		meta,
	});

	const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
	// Automatically fetch all pages when opted in so consumers get the complete list of boards
	useEffect(() => {
		if (autoFetchAll && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [autoFetchAll, hasNextPage, isFetchingNextPage, fetchNextPage]);

	return query;
}

export const invalidateBoardsQuery = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_API_KEY] });
};
